import { DateTime, Result as Either, Schema } from "effect";

/**
 * The only policy evidence allowed to persist on a FrontDesk+ kiosk.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { DateTime } from "effect";
 *
 * const snapshot: KioskEmergencyCacheSnapshot = {
 *   enabled: true,
 *   policyVersion: 2,
 *   confirmedAt: DateTime.makeUnsafe("2026-07-29T12:00:00.000Z"),
 * };
 * ```
 */
export const KioskEmergencyCacheSnapshot = Schema.Struct({
  enabled: Schema.Literal(true),
  policyVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(1))),
  confirmedAt: Schema.DateTimeUtcFromString,
});

/**
 * A decoded, patient-free emergency-policy cache snapshot.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const version: KioskEmergencyCacheSnapshot["policyVersion"] = 1;
 * ```
 */
export type KioskEmergencyCacheSnapshot =
  typeof KioskEmergencyCacheSnapshot.Type;

/**
 * The JSON-safe representation stored by platform adapters.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const encoded: KioskEmergencyCacheSnapshotEncoded = {
 *   enabled: true,
 *   policyVersion: 2,
 *   confirmedAt: "2026-07-29T12:00:00.000Z",
 * };
 * ```
 */
export type KioskEmergencyCacheSnapshotEncoded =
  typeof KioskEmergencyCacheSnapshot.Encoded;

const decodeSnapshot = Schema.decodeUnknownResult(KioskEmergencyCacheSnapshot, {
  onExcessProperty: "error",
});

/**
 * Decodes one platform-storage value, returning no permission for invalid data.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const snapshot = decodeKioskEmergencyCacheSnapshot(null);
 * ```
 */
export function decodeKioskEmergencyCacheSnapshot(
  input: unknown,
): KioskEmergencyCacheSnapshot | undefined {
  const decoded = decodeSnapshot(input);
  return Either.isSuccess(decoded) ? decoded.success : undefined;
}

/**
 * Decodes a JSON cache entry, returning no permission for invalid JSON or data.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const snapshot = decodeKioskEmergencyCacheJson(null);
 * ```
 */
export function decodeKioskEmergencyCacheJson(
  input: string | null,
): KioskEmergencyCacheSnapshot | undefined {
  if (input === null) return undefined;

  try {
    const parsed: unknown = JSON.parse(input);
    return decodeKioskEmergencyCacheSnapshot(parsed);
  } catch {
    return undefined;
  }
}

/**
 * Encodes a decoded cache snapshot for IPC or JSON persistence.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { DateTime } from "effect";
 *
 * const encoded = encodeKioskEmergencyCacheSnapshot({
 *   enabled: true,
 *   policyVersion: 2,
 *   confirmedAt: DateTime.makeUnsafe("2026-07-29T12:00:00.000Z"),
 * });
 * ```
 */
export function encodeKioskEmergencyCacheSnapshot(
  snapshot: KioskEmergencyCacheSnapshot,
): KioskEmergencyCacheSnapshotEncoded {
  return Schema.encodeSync(KioskEmergencyCacheSnapshot)(snapshot);
}

/**
 * Creates a confirmed emergency snapshot from current server policy truth.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const snapshot = createKioskEmergencyCacheSnapshot(3, new Date());
 * ```
 */
export function createKioskEmergencyCacheSnapshot(
  policyVersion: number,
  confirmedAt: Date,
): KioskEmergencyCacheSnapshot {
  return {
    enabled: true,
    policyVersion,
    confirmedAt: DateTime.makeUnsafe(confirmedAt),
  };
}
